import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import SEO from '@/components/base/SEO';
import { COMICS, CATEGORIES } from '@/mocks/comics';

export default function CategoryPage() {
  const { category } = useParams<{ category: string }>();
  const currentSlug = category || 'all';
  const currentCategory = CATEGORIES.find((c) => c.slug === currentSlug) || CATEGORIES[0];
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const filteredComics = useMemo(() => {
    let result = currentSlug === 'all'
      ? [...COMICS]
      : COMICS.filter((c) => c.categories.includes(currentSlug));

    switch (sortBy) {
      case 'popular':
        return result.sort((a, b) => b.views - a.views);
      case 'rating':
        return result.sort((a, b) => b.rating - a.rating);
      case 'latest':
      default:
        return result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
  }, [currentSlug, sortBy]);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.koreancomic.cn/' },
          { '@type': 'ListItem', position: 2, name: currentCategory.name, item: `https://www.koreancomic.cn/category/${currentSlug}` },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${currentCategory.name}韩漫 - 韩国漫画网`,
        url: `https://www.koreancomic.cn/category/${currentSlug}`,
        description: `浏览${currentCategory.name}类韩漫作品，韩国漫画网提供免费无删减的${currentCategory.name}韩国漫画在线阅读。`,
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${currentCategory.name}韩漫 - 韩国漫画网`}
        description={`浏览${currentCategory.name}类韩漫作品，韩国漫画网提供免费无删减的${currentCategory.name}韩国漫画在线阅读，资源持续更新。`}
        keywords={`${currentCategory.name}韩漫,韩漫在线阅读,免费韩国漫画,${currentCategory.name}漫画`}
        schema={categorySchema}
      />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-700">{currentCategory.name}</span>
          </nav>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950">{currentCategory.name}韩漫</h1>
            <p className="text-sm text-foreground-500 mt-2">{currentCategory.description}</p>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-6">
            {CATEGORIES.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  currentSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-500">
              共 <strong className="text-foreground-800">{filteredComics.length}</strong> 部作品
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {[
                { key: 'latest', label: '最新' },
                { key: 'popular', label: '人气' },
                { key: 'rating', label: '评分' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key as typeof sortBy)}
                  className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-background-50 text-foreground-950 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5 pb-14" data-product-shop>
            {filteredComics.map((comic) => (
              <Link
                key={comic.id}
                to={`/article/${comic.id}`}
                className="group block"
              >
                <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - ${currentCategory.name}韩漫`}
                    title={`${comic.title} 韩国漫画网 韩漫在线阅读`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-2 left-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      comic.status === '连载中'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-secondary-500 text-background-50'
                    }`}>
                      {comic.status}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                    <div className="flex items-center gap-1 text-white text-xs">
                      <i className="ri-star-fill text-yellow-400" />
                      <span>{comic.rating}</span>
                      <span className="mx-1">·</span>
                      <span>{comic.chapters.length}话</span>
                    </div>
                  </div>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                  {comic.title}
                </h3>
                <p className="text-xs text-foreground-500 mt-1 line-clamp-1">{comic.author} 著</p>
              </Link>
            ))}
          </div>

          {filteredComics.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-4xl text-foreground-300" />
              <p className="text-foreground-500 mt-3">该分类下暂无作品</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}